import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '@/utils/cn';

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  id?: string;
}

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  className,
  id,
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);

  const allSelected = options.length > 0 && value.length === options.length;

  const handleClickOutside = useCallback((e: MouseEvent) => {
    const target = e.target as Node;
    const insideButton = containerRef.current?.contains(target) ?? false;
    const insideDropdown = dropdownRef.current?.contains(target) ?? false;
    if (!insideButton && !insideDropdown) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClickOutside]);

  // Recalculate dropdown position on open and on scroll/resize
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const updatePosition = () => {
      const rect = buttonRef.current!.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        e.stopPropagation();
        setIsOpen(false);
      }
    },
    [isOpen],
  );

  function toggleOption(optionValue: string) {
    const next = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange(next);
    setIsOpen(false);
  }

  function toggleAll() {
    onChange(allSelected ? [] : options.map((o) => o.value));
  }

  function removeValue(optionValue: string) {
    onChange(value.filter((v) => v !== optionValue));
  }

  const selectedLabels = options.filter((o) => value.includes(o.value));

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        onKeyDown={handleKeyDown}
        className="flex min-h-8 w-full items-start justify-between border border-cool-gray-2 bg-white px-2 py-1 text-xs font-normal focus:border-black focus:outline-none focus:ring-0"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="flex flex-1 flex-col gap-0.5 min-w-0">
          {selectedLabels.length === 0 ? (
            <span className="text-cool-gray-10">{placeholder}</span>
          ) : (
            selectedLabels.map((opt) => (
              <span
                key={opt.value}
                className="inline-flex items-center gap-1 bg-gray-100 px-2 py-0.5 text-xs text-black"
              >
                {opt.label}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeValue(opt.value);
                  }}
                  className="text-cool-gray-10 hover:text-black"
                  aria-label={`Remove ${opt.label}`}
                >
                  <X size={12} />
                </button>
              </span>
            ))
          )}
        </span>
        <ChevronDown
          size={16}
          className={cn(
            'ml-2 mt-0.5 shrink-0 self-start text-cool-gray-10 transition-transform',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isOpen && createPortal(
        <ul
          ref={dropdownRef}
          role="listbox"
          aria-multiselectable="true"
          style={dropdownStyle}
          className="max-h-60 overflow-auto border border-cool-gray-2 bg-white shadow-lg"
        >
          {/* Select All */}
          <li className="border-b border-cool-gray-2 px-3 py-2">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                className="h-4 w-4 border-cool-gray-2 text-dds-green focus:ring-dds-green"
              />
              Select All
            </label>
          </li>

          {options.map((opt) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={value.includes(opt.value)}
              className="px-3 py-2 hover:bg-gray-50"
            >
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={value.includes(opt.value)}
                  onChange={() => toggleOption(opt.value)}
                  className="h-4 w-4 border-cool-gray-2 text-dds-green focus:ring-dds-green"
                />
                {opt.label}
              </label>
            </li>
          ))}
        </ul>,
        document.body,
      )}
    </div>
  );
}
